(ns client-portal.client.ai-agent.history.views
  "The Agent view's right panel: the user's past chats with the selected agent. Fronted
   on every general agent route (see client.ai-agent.views/root), so it is there whether
   or not a conversation is open; the New Chat button only appears once a session is in
   the URL, since without one the view IS a new chat."
  (:require [client-portal.client.ai-agent.history.data]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.components.components :as c]
            [client-portal.client.layout :as l]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.time :as time]
            [shared.core.util :as util]))

(defn session-row [{:keys [id title concept-id concept-label location-id location-label
                           last-modified-at active? pending?]}]
  (let [selected-background (when (or active? pending?)
                              (style/alc-color "Background/active_secondary"))]
    [c/interactive
     :div {:style {:background-color (or selected-background
                                         (style/alc-color "Background/primary"))
                   :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                   :border-radius 6
                   :cursor (if pending? :default :pointer)
                   :display :flex
                   ;; A row in a scrolling flex column: without this it shrinks to fit the
                   ;; panel instead of overflowing it, and its `overflow: hidden` text
                   ;; (which drops the automatic minimum size) clips away to nothing.
                   :flex "0 0 auto"
                   :flex-direction :column
                   :gap 4
                   :padding "10px 12px"
                   :transition "0.2s"
                   :width "100%"}
           :hover-style {:background-color (or selected-background
                                               (style/alc-color "Background/hover"))}
           :on-click (when-not pending?
                       #(re/dispatch [:ai-agent/open-session {:id id
                                                              :concept-id concept-id
                                                              :location-id location-id}]))}
     [:span {:style {:color (style/alc-color "Text/primary_header")
                     :display "-webkit-box"
                     :-webkit-line-clamp 2
                     :-webkit-box-orient "vertical"
                     :overflow :hidden
                     :font-size 14
                     :font-weight 400
                     :line-height 1.43}}
      (or (not-empty title) "Untitled chat")]
     [:span {:style {:color (style/alc-color "Text/secondary")
                     :font-size 12
                     :line-height 1.33
                     :overflow :hidden
                     :text-overflow :ellipsis
                     :white-space :nowrap}}
      (->> [(time/relative-time-label last-modified-at) concept-label location-label]
           (remove s/blank?)
           (s/join " · "))]]))

(defn history-list []
  (let [sessions- (re/subscribe [:ai-agent/session-history-])
        loading?- (re/subscribe [:ai-agent/session-history-loading?-])
        has-more?- (re/subscribe [:ai-agent/session-history-has-more?-])
        cur-session-id- (re/subscribe [:ai-agent/current-session-id-])
        agent-list- (re/subscribe [:core/ai-agent-list-])
        agent-id- (re/subscribe [:ai-agent/resolved-agent-id-])]
    (fn []
      (let [sessions @sessions-
            cur-session-id @cur-session-id-]
        [:div {:id "ai-agent-history-scroll"
               :on-scroll (fn [e]
                            (when (and @has-more?-
                                       (not @loading?-)
                                       (util/scrolled-near-bottom? (.-target e) 200))
                              (re/dispatch [:ai-agent/load-session-history])))
               :style {:display :flex
                       :flex-direction :column
                       :gap 8
                       :flex 1
                       :overflow :auto
                       :padding "12px 4px"
                       :scrollbar-gutter "stable both-edges"}}
         (cond
           (and (empty? sessions) (or @loading?- (util/loading? @agent-list-)))
           [alc/up-loader-with-label {:label "Loading History..."
                                      :style {:flex 1
                                              :height :auto
                                              :min-height :auto}}]

           (empty? @agent-id-)
           [empty-state/no-agent-card {:style {:padding "24px 12px"}}]

           (empty? sessions)
           [empty-state/no-data-card {:material-icon-type :chat-bubble-outline
                                      :text "No Past Chats"
                                      :style {:padding "24px 12px"}}]

           :else
           [:<>
            (for [{:keys [id] :as session} sessions]
              ^{:key id}
              [session-row (assoc session :active? (= id cur-session-id))])
            (when @loading?-
              [:div {}
               [alc/up-loader-with-label {:label "Loading More..."
                                          :loader-size "small"
                                          :style {:flex-direction :row
                                                  :padding 20}}]])])]))))

(defn history-panel []
  (let [cur-session-id- (re/subscribe [:ai-agent/url-session-id-])]
    (fn []
      [:div {:style {:display :flex
                     :flex-direction :column
                     :height "100%"}}
       [:div {:style {:background-color (style/alc-color "Background/secondary")
                      :flex "0 0 auto"
                      :display :flex
                      :align-items :center
                      :gap 8
                      :padding "12px 16px"
                      :min-height 58
                      :box-sizing :border-box
                      :z-index (inc (style/z :medium))}}
        [:span {:style {:color (style/alc-color "Text/primary_header")
                        :display :flex
                        :align-items :center
                        :font-size 20
                        :font-weight 400
                        :line-height 1.4}}
         "Chat History"]
        [c/spacer]
        ;; Without a session in the URL the view is already a new chat.
        (when (not-empty @cur-session-id-)
          [alc/tooltip
           {:target-content [alc/up-button {:icon-type :AddIcon
                                            :rounded? true
                                            :on-click #(re/dispatch [:ai-agent/start-new-chat])}]
            :tooltip-content "New Chat"}])]
       [history-list]])))

(defmethod l/panel-right-view
  :ai-agent/history-panel [_]
  [history-panel])
